import { RotateCw, X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import {
  isWhole,
  ratioCrop,
  readCrop,
  squareCrop,
  turnCrop,
  turnedSize,
  wholeCrop,
  type Crop,
  type Tone,
} from '../../shared/crop'
import { dragCrop, type Handle } from '@/lib/crop'
import { makeCopies, openPhoto, type Copies } from '@/lib/photos'
import { cn } from '@/lib/utils'

type Size = { width: number; height: number }

// The shapes a crop is held to: the photo's own, as it's turned, or a square.
type Shape = 'original' | 'square'

const ratioOf = (shape: Shape, frame: Size) => (shape === 'square' ? 1 : frame.width / frame.height)

// Turning, cropping and toning a photo: the whole photo in a dark room with the part that will show lit, to turn a
// quarter at a time, crop by its corners to the photo's own shape or a square, and show in color, black and white, or
// sepia. The original is opened here and never
// changed: the copies people see are made again from it, turned and cropped, and handed to onSave with the crop (null
// when it's the whole photo as it was).
export function CropPhoto({
  open,
  onOpenChange,
  source,
  crop: kept,
  shape: firstShape = 'original',
  medium = false,
  adding = false,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  // The original: its address, or the file itself before it's uploaded.
  source: string | Blob
  crop: Crop | null
  shape?: Shape
  // Album photos keep a medium copy too.
  medium?: boolean
  // A photo being added is saved even uncropped, and its button says so.
  adding?: boolean
  onSave: (crop: Crop | null, copies: Copies, size: Size) => Promise<void>
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby="crop-hint"
          // Dragging a corner near the edge shouldn't count as a tap outside.
          onPointerDownOutside={(e) => e.preventDefault()}
          className="fixed inset-0 z-[60] flex flex-col bg-viewer text-viewer-foreground duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          {open && (
            <Editor
              source={source}
              kept={kept}
              firstShape={firstShape}
              medium={medium}
              adding={adding}
              onSave={onSave}
              onClose={() => onOpenChange(false)}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

type Opened = { bitmap: ImageBitmap; preview: string }

function Editor({
  source,
  kept,
  firstShape,
  medium,
  adding,
  onSave,
  onClose,
}: {
  source: string | Blob
  kept: Crop | null
  firstShape: Shape
  medium: boolean
  adding: boolean
  onSave: (crop: Crop | null, copies: Copies, size: Size) => Promise<void>
  onClose: () => void
}) {
  const [opened, setOpened] = useState<Opened | null>(null)
  const [failed, setFailed] = useState('')
  const [crop, setCrop] = useState<Crop | null>(null)
  const [shape, setShape] = useState<Shape>(firstShape)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // The original, opened once: drawn small for the screen, and kept whole for making the copies.
  useEffect(() => {
    let live = true
    let done: Opened | null = null
    void (async () => {
      try {
        const blob =
          typeof source === 'string'
            ? await fetch(source, { credentials: 'same-origin' }).then((res) => {
                if (!res.ok) throw new Error('not found')
                return res.blob()
              })
            : source
        const bitmap = await openPhoto(blob)
        const preview = await previewOf(bitmap)
        done = { bitmap, preview }
        if (!live) return void release(done)
        setOpened(done)
        const { width, height } = bitmap
        const start = (kept && readCrop(kept, width, height)) || null
        if (start && start.width === start.height) setShape('square')
        setCrop(start ?? (firstShape === 'square' ? squareCrop(width, height) : wholeCrop(width, height)))
      } catch {
        if (live) setFailed('This photo couldn’t be opened for editing. Please try again.')
      }
    })()
    return () => {
      live = false
      if (done) release(done)
    }
    // The photo is opened once each time the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The original's size, and the size it is as turned, which the crop is measured in.
  const size = opened ? { width: opened.bitmap.width, height: opened.bitmap.height } : null
  const frame = size && crop ? turnedSize(size.width, size.height, crop.turn) : null
  const ratio = frame ? ratioOf(shape, frame) : 1

  async function save() {
    if (!opened || !crop || !size) return
    const chosen = isWhole(crop, size.width, size.height) ? null : crop
    // Nothing changed: nothing to make again.
    if (!adding && sameCrop(chosen, kept)) return onClose()
    setSaving(true)
    setError('')
    try {
      await onSave(chosen, await makeCopies(opened.bitmap, chosen, medium), size)
      onClose()
    } catch (e) {
      setError((e as Error).message)
      setSaving(false)
    }
  }

  // A shape takes the biggest part of it the photo holds, centred: Original is the whole photo again.
  function pick(next: Shape) {
    setShape(next)
    if (!frame || !crop) return
    setCrop({ ...crop, ...ratioCrop(frame.width, frame.height, ratioOf(next, frame)) })
  }

  // A quarter turn clockwise each time; four bring it back.
  function turn() {
    if (size && crop) setCrop(turnCrop(crop, size.width, size.height, 1))
  }

  return (
    <>
      <div className="flex items-center gap-2 px-3 pt-[calc(var(--safe-top)+0.75rem)] pb-2 sm:px-5">
        <DialogPrimitive.Close aria-label="Cancel editing" className={cn(round, 'hover:bg-viewer-foreground/12')}>
          <X aria-hidden />
        </DialogPrimitive.Close>
        <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-center text-sm font-medium">
          Rotate, crop and color
        </DialogPrimitive.Title>
        <button
          type="button"
          disabled={!crop || saving}
          onClick={() => void save()}
          className="min-h-11 rounded-full bg-viewer-foreground px-5 text-sm font-medium text-viewer transition-opacity outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-50"
        >
          {saving ? 'Saving…' : adding ? 'Add' : 'Save'}
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        {opened && crop && size && frame ? (
          <Stage preview={opened.preview} original={size} frame={frame} crop={crop} ratio={ratio} onCrop={setCrop} />
        ) : (
          <p role="status" className="absolute inset-0 grid place-items-center px-6 text-center text-viewer-muted">
            {failed || 'Opening the photo…'}
          </p>
        )}
      </div>

      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 pt-3 pb-[calc(var(--safe-bottom)+1rem)]">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={!crop}
            onClick={turn}
            className="flex min-h-11 items-center gap-2 rounded-full bg-viewer-foreground/10 px-4 text-sm font-medium outline-none hover:bg-viewer-foreground/16 focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-50"
          >
            <RotateCw className="size-5" aria-hidden /> Rotate
          </button>
          <div role="group" aria-label="Shape" className="flex rounded-full bg-viewer-foreground/10 p-1">
            {(
              [
                ['original', 'Original'],
                ['square', 'Square'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={shape === value}
                disabled={!crop}
                onClick={() => pick(value)}
                className={cn(
                  'min-h-9 rounded-full px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
                  shape === value ? 'bg-viewer-foreground text-viewer' : 'hover:bg-viewer-foreground/12',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* Each tone shown on the photo itself, so the choice needs no explaining. */}
        <div role="group" aria-label="Color" className="grid grid-cols-3 gap-3">
          {TONE_CHOICES.map(([tone, label]) => {
            const on = (crop?.tone ?? null) === tone
            return (
              <button
                key={label}
                type="button"
                aria-pressed={on}
                disabled={!crop}
                onClick={() => crop && setCrop(withTone(crop, tone))}
                className="group flex flex-col items-center gap-1.5 rounded-xl text-xs font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
              >
                <span
                  className={cn(
                    'block aspect-[4/3] w-full overflow-hidden rounded-lg ring-2 ring-offset-2 ring-offset-viewer transition-shadow',
                    on ? 'ring-viewer-foreground' : 'ring-transparent',
                  )}
                >
                  {opened && (
                    <img
                      src={opened.preview}
                      alt=""
                      className="size-full object-cover"
                      style={{ filter: FILTERS[tone ?? 'color'] }}
                    />
                  )}
                </span>
                <span className={on ? 'text-viewer-foreground' : 'text-viewer-muted'}>{label}</span>
              </button>
            )
          })}
        </div>
        {error ? (
          <p role="alert" className="text-center text-sm text-viewer-foreground">
            {error}
          </p>
        ) : (
          <p id="crop-hint" className="text-center text-sm text-viewer-muted">
            Drag a corner to crop. Your original is always kept.
          </p>
        )}
      </div>
    </>
  )
}

const CORNERS: Handle[] = ['nw', 'ne', 'sw', 'se']

// The photo, turned, fitted whole into the space there is, with the crop drawn over it.
function Stage({
  preview,
  original,
  frame: size,
  crop,
  ratio,
  onCrop,
}: {
  preview: string
  original: Size
  // The photo's size as turned.
  frame: Size
  crop: Crop
  ratio: number
  onCrop: (crop: Crop) => void
}) {
  const stage = useRef<HTMLDivElement>(null)
  const [room, setRoom] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    const el = stage.current
    if (!el) return
    const measure = () => setRoom({ width: el.clientWidth, height: el.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  // Screen pixels per pixel of the photo.
  const scale = Math.min(room.width / size.width, room.height / size.height) || 0

  // A drag starts on the crop or one of its handles (each names itself in data-handle) and is followed from there.
  const drag = useRef<{ handle: Handle; start: Crop; x: number; y: number } | null>(null)
  function onPointerDown(e: PointerEvent<HTMLElement>) {
    const handle = (e.target as HTMLElement).closest<HTMLElement>('[data-handle]')?.dataset.handle as Handle | undefined
    if (!handle) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { handle, start: crop, x: e.clientX, y: e.clientY }
  }
  function onPointerMove(e: PointerEvent<HTMLElement>) {
    const d = drag.current
    if (!d || !scale) return
    onCrop(dragCrop(d.start, d.handle, (e.clientX - d.x) / scale, (e.clientY - d.y) / scale, size, ratio))
  }
  const onPointerUp = () => (drag.current = null)

  // Arrow keys move the crop; with Shift they make it bigger (right, down) or smaller (left, up).
  function onKey(e: KeyboardEvent) {
    const step = Math.max(1, Math.round(Math.max(size.width, size.height) / 100))
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    const move = arrows[e.key]
    if (!move) return
    e.preventDefault()
    let [dx, dy] = move
    if (e.shiftKey) dx = dy = dx + dy
    onCrop(dragCrop(crop, e.shiftKey ? 'se' : 'move', dx, dy, size, ratio))
  }

  const box = {
    left: crop.x * scale,
    top: crop.y * scale,
    width: crop.width * scale,
    height: crop.height * scale,
  }
  return (
    <div ref={stage} className="absolute inset-x-6 inset-y-4 flex touch-none items-center justify-center select-none">
      {scale > 0 && (
        <div
          className="relative"
          style={{ width: size.width * scale, height: size.height * scale }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* The preview is the original as it stands, turned about its centre to fill the frame. */}
          <img
            src={preview}
            alt=""
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={{
              width: original.width * scale,
              height: original.height * scale,
              left: ((size.width - original.width) * scale) / 2,
              top: ((size.height - original.height) * scale) / 2,
              transform: `rotate(${(crop.turn ?? 0) * 90}deg)`,
              filter: FILTERS[crop.tone ?? 'color'],
            }}
          />
          <div className="absolute inset-0 overflow-hidden">
            <div
              role="group"
              tabIndex={0}
              aria-label="The part that will show. Arrow keys move it; Shift and the arrow keys resize it."
              onKeyDown={onKey}
              data-handle="move"
              className="absolute cursor-move shadow-[0_0_0_100vmax_rgb(0_0_0/0.6)] outline-2 -outline-offset-1 outline-white/90 focus-visible:outline-[3px]"
              style={box}
            >
              {/* Thirds, to line things up by. */}
              <span aria-hidden className="pointer-events-none absolute inset-y-0 left-1/3 w-px bg-white/35" />
              <span aria-hidden className="pointer-events-none absolute inset-y-0 left-2/3 w-px bg-white/35" />
              <span aria-hidden className="pointer-events-none absolute inset-x-0 top-1/3 h-px bg-white/35" />
              <span aria-hidden className="pointer-events-none absolute inset-x-0 top-2/3 h-px bg-white/35" />
            </div>
          </div>
          {/* Handles sit over the photo's edge, so a corner at the edge of the photo can still be held. */}
          {CORNERS.map((handle) => (
            <span
              key={handle}
              aria-hidden
              data-handle={handle}
              className={cn(
                'absolute z-10 grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center',
                CURSOR[handle],
              )}
              style={{
                left: box.left + (handle.includes('w') ? 0 : handle.includes('e') ? box.width : box.width / 2),
                top: box.top + (handle.includes('n') ? 0 : handle.includes('s') ? box.height : box.height / 2),
              }}
            >
              <span className={cn('absolute border-white', MARK[handle])} />
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const CURSOR: Record<Handle, string> = {
  move: 'cursor-move',
  n: 'cursor-ns-resize',
  s: 'cursor-ns-resize',
  e: 'cursor-ew-resize',
  w: 'cursor-ew-resize',
  nw: 'cursor-nwse-resize',
  se: 'cursor-nwse-resize',
  ne: 'cursor-nesw-resize',
  sw: 'cursor-nesw-resize',
}

// What each handle looks like: an L hugging its corner, or a short bar along its side. Each starts at the centre of
// its handle, which is on the crop's own corner or side.
const MARK: Record<Handle, string> = {
  move: '',
  nw: 'top-1/2 left-1/2 size-5 border-t-[3px] border-l-[3px]',
  ne: 'top-1/2 right-1/2 size-5 border-t-[3px] border-r-[3px]',
  sw: 'bottom-1/2 left-1/2 size-5 border-b-[3px] border-l-[3px]',
  se: 'right-1/2 bottom-1/2 size-5 border-r-[3px] border-b-[3px]',
  n: 'top-1/2 left-1/2 h-0 w-6 -translate-x-1/2 border-t-[3px]',
  s: 'bottom-1/2 left-1/2 h-0 w-6 -translate-x-1/2 border-b-[3px]',
  e: 'top-1/2 right-1/2 h-6 w-0 -translate-y-1/2 border-r-[3px]',
  w: 'top-1/2 left-1/2 h-6 w-0 -translate-y-1/2 border-l-[3px]',
}

const TONE_CHOICES = [
  [null, 'Color'],
  ['mono', 'Black and white'],
  ['sepia', 'Sepia'],
] as const

function withTone(crop: Crop, tone: Tone | null): Crop {
  const next = { ...crop, tone: tone ?? undefined }
  if (!tone) delete next.tone
  return next
}

const FILTERS = { color: undefined, mono: 'grayscale(1)', sepia: 'sepia(1)' } as const

const round =
  'grid size-11 shrink-0 place-items-center rounded-full outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/60'

const sameCrop = (a: Crop | null, b: Crop | null) =>
  a === b ||
  (!!a &&
    !!b &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    (a.turn ?? 0) === (b.turn ?? 0) &&
    a.tone === b.tone)

// A copy of the photo small enough to draw quickly, sharp on a big screen.
async function previewOf(bitmap: ImageBitmap) {
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85))
  if (!blob) throw new Error('no preview')
  return URL.createObjectURL(blob)
}

function release({ bitmap, preview }: Opened) {
  bitmap.close()
  URL.revokeObjectURL(preview)
}
